
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { Button, Input, Card } from '../components/UI';
import { Eye, EyeOff, Shield, Globe, Lock, Check, Package, Building } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';

const Login: React.FC = () => {
  const { login } = useAuth();
  const { t, language, setLanguage } = useTranslation();
  
  const [loginMode, setLoginMode] = useState<'enterprise' | 'supplier'>('enterprise');
  
  // Separate states for demo convenience
  const [entEmail, setEntEmail] = useState('support@io-market.com');
  const [supEmail, setSupEmail] = useState('support@io-market.com');
  const [password, setPassword] = useState('password123');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const emailToUse = loginMode === 'enterprise' ? entEmail : supEmail;
    
    // Simulate API call
    setTimeout(() => {
      login(emailToUse, loginMode);
      setIsLoading(false);
    }, 1200);
  };

  return (
    <div className="min-h-screen w-full bg-[#F4F4F4] dark:bg-[#050505] flex flex-col items-center justify-center p-6 relative overflow-hidden font-sans">
      
      {/* Background Decor */}
      <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-[#474D22] opacity-5 blur-[120px] rounded-full pointer-events-none"></div>
      
      <div className="absolute top-0 right-0 p-8 z-10">
        <button 
          onClick={() => setLanguage(language === 'en' ? 'de' : 'en')}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#161616] border border-gray-200 dark:border-gray-700 text-[10px] font-bold uppercase tracking-wider text-[#474D22] dark:text-white hover:border-[#474D22] transition-colors shadow-sm"
        >
          <Globe size={14} />
          {language === 'en' ? 'Deutsch' : 'English'}
        </button>
      </div>

      <div className="w-full max-w-[420px] z-10">
        <div className="flex flex-col items-center mb-8 text-center">
          <div className="w-16 h-16 bg-[#474D22] flex items-center justify-center mb-6 shadow-xl">
             <BrandLogo type="main" size={32} />
          </div>
          <h1 className="text-2xl font-semibold text-[#161616] dark:text-white tracking-tight">
             VMI Hub
          </h1>
          <p className="text-sm font-medium text-gray-500 uppercase tracking-widest mt-2">
            {loginMode === 'enterprise' ? 'Enterprise Access' : 'Supplier Portal'}
          </p>
        </div>

        <Card className="p-0 border-t-4 border-t-[#474D22] shadow-xl bg-white dark:bg-[#111111] overflow-hidden">
          {/* Mode Toggle Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={() => setLoginMode('enterprise')}
              className={`flex-1 py-4 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${
                loginMode === 'enterprise' 
                  ? 'bg-white dark:bg-[#111111] text-[#474D22] dark:text-white' 
                  : 'bg-gray-50 dark:bg-[#1A1A1A] text-gray-400 hover:bg-gray-100 dark:hover:bg-[#222]'
              }`}
            >
              <Building size={14} /> Enterprise
            </button>
            <button
              onClick={() => setLoginMode('supplier')}
              className={`flex-1 py-4 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${
                loginMode === 'supplier' 
                  ? 'bg-white dark:bg-[#111111] text-[#474D22] dark:text-white' 
                  : 'bg-gray-50 dark:bg-[#1A1A1A] text-gray-400 hover:bg-gray-100 dark:hover:bg-[#222]'
              }`}
            >
              <Package size={14} /> Supplier
            </button>
          </div>

          <div className="p-10">
            <div className="mb-8">
               <h2 className="text-xl font-bold text-[#161616] dark:text-white">
                 {loginMode === 'enterprise' ? t('sign_in') : t('sign_in_supplier')}
               </h2>
               <p className="text-sm text-gray-500 mt-1">
                 {loginMode === 'enterprise' 
                   ? 'Access your inventory management dashboard' 
                   : 'View customer stock levels and consignment needs'}
               </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  {loginMode === 'enterprise' ? 'Corporation ID / Email' : 'Supplier Email'}
                </label>
                <Input 
                  type="email" 
                  value={loginMode === 'enterprise' ? entEmail : supEmail} 
                  onChange={(e) => loginMode === 'enterprise' ? setEntEmail(e.target.value) : setSupEmail(e.target.value)}
                  required
                  className="h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                  placeholder="name@company.com"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Password</label>
                  <button type="button" className="text-[10px] font-bold uppercase tracking-widest text-[#474D22] hover:underline">
                    Forgot?
                  </button>
                </div>
                <div className="relative">
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    placeholder="••••••••"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#474D22] transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <div className="relative flex items-center">
                  <input type="checkbox" id="remember" className="peer h-4 w-4 cursor-pointer appearance-none border border-gray-300 checked:border-[#474D22] checked:bg-[#474D22] transition-all" />
                  <Check size={12} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100" strokeWidth={4} />
                </div>
                <label htmlFor="remember" className="text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer select-none">Trust this device for 30 days</label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-xs font-bold tracking-[0.15em] shadow-lg mt-4"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                    AUTHENTICATING...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock size={14} /> SECURE LOGIN
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 text-center">
              <p className="text-xs text-gray-500 font-medium">
                New to the gateway? <Link to="/signup" className="text-[#474D22] dark:text-[#717C26] font-bold underline underline-offset-4">Create an Account</Link>
              </p>
            </div>
          </div>
        </Card>

        <div className="mt-8 flex items-center justify-center gap-2 text-gray-400 dark:text-gray-600 opacity-60">
           <Shield size={12} />
           <span className="text-[10px] font-bold uppercase tracking-widest">Secured by VMI Shield</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
